import { createContext, useContext, useState, useCallback, useMemo, memo, ReactNode, MouseEvent } from 'react';
import { FaCloudUploadAlt, FaChevronUp } from 'react-icons/fa';
import UploadProgress from '../../components/UploadProgress/UploadProgress';
import {
    UploadProgressProviderProps,
    UploadProgressContextType,
    UploadProgressActionsContextType,
    UploadProgressStateContextType
} from './types';
import { useUploadProgressProvider } from './useUploadProgressProvider';
import styles from './UploadProgressProvider.module.css';

const MemoizedChildren = memo(({ children }: { children: ReactNode }) => <>{children}</>);
MemoizedChildren.displayName = 'MemoizedChildren';

// 空の処理を行うデフォルト関数
const noopUploadProgressActions: UploadProgressActionsContextType = {
    startUpload: async () => { },
    pauseUpload: () => { },
    resumeUpload: async () => { },
    cancelUpload: () => { },
    closeUploadProgress: () => { }
};

const noopUploadProgressState: UploadProgressStateContextType = {
    uploadProgresses: [],
    isMinimized: false,
    toggleMinimize: () => { }
};

// アップロード進捗コンテキストの作成
const UploadProgressActionsContext = createContext<UploadProgressActionsContextType>(noopUploadProgressActions);
const UploadProgressStateContext = createContext<UploadProgressStateContextType>(noopUploadProgressState);

/**
 * アップロード進捗を管理するためのフック
 */
export const useUploadProgressActions = (): UploadProgressActionsContextType => {
    return useContext(UploadProgressActionsContext);
};

export const useUploadProgressState = (): UploadProgressStateContextType => {
    return useContext(UploadProgressStateContext);
};

export const useUploadProgress = (): UploadProgressContextType => {
    const actions = useUploadProgressActions();
    const state = useUploadProgressState();
    return useMemo(() => ({ ...actions, ...state }), [actions, state]);
};

/**
 * アプリケーション全体でアップロード進捗を管理・表示するためのプロバイダーコンポーネント
 */
export const UploadProgressProvider = ({ children }: UploadProgressProviderProps) => {
    const {
        startUpload,
        pauseUpload,
        resumeUpload,
        cancelUpload,
        closeUploadProgress,
        uploadProgresses
    } = useUploadProgressProvider();

    // 最小化状態の管理
    const [isMinimized, setIsMinimized] = useState(false);
    const toggleMinimize = useCallback(() => {
        setIsMinimized(prev => !prev);
    }, []);

    // 表示すべき進捗のみをフィルタリング
    const visibleProgresses = useMemo(() => uploadProgresses.filter(p => p.open), [uploadProgresses]);
    const dismissableProgresses = useMemo(
        () => uploadProgresses.filter(p => ['completed', 'error', 'cancelled'].includes(p.status)),
        [uploadProgresses]
    );
    const dismissableCount = dismissableProgresses.length;

    // 進行中アイテムの総合進捗計算
    const totalProgress = uploadProgresses.length > 0
        ? Math.round(uploadProgresses.reduce((sum, p) => sum + p.progress, 0) / uploadProgresses.length)
        : 0;
    const shouldShowMinimized = isMinimized || visibleProgresses.length === 0;

    const actionsValue = useMemo<UploadProgressActionsContextType>(() => ({
        startUpload,
        pauseUpload,
        resumeUpload,
        cancelUpload,
        closeUploadProgress
    }), [startUpload, pauseUpload, resumeUpload, cancelUpload, closeUploadProgress]);

    const stateValue = useMemo<UploadProgressStateContextType>(() => ({
        uploadProgresses,
        isMinimized,
        toggleMinimize
    }), [uploadProgresses, isMinimized, toggleMinimize]);

    const clearDismissable = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        dismissableProgresses.forEach((progress) => {
            closeUploadProgress(progress.id);
        });
    }, [dismissableProgresses, closeUploadProgress]);

    return (
        <UploadProgressActionsContext.Provider value={actionsValue}>
            <UploadProgressStateContext.Provider value={stateValue}>
                <MemoizedChildren>{children}</MemoizedChildren>
                {/* フローティングUI - 表示すべきものがある場合のみ可視 */}
                {uploadProgresses.length > 0 && (
                    <div className={styles['upload-progress-provider-container']}>
                        {shouldShowMinimized ? (
                            // 最小化状態: 小さなバッジを表示
                            <div
                                className={styles['minimized-container']}
                                onClick={toggleMinimize}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && toggleMinimize()}
                            >
                                <div className={styles['minimized-main']}>
                                    <FaCloudUploadAlt size={18} />
                                    <span>{uploadProgresses.length}件のアップロード ({totalProgress}%)</span>
                                    <FaChevronUp size={14} />
                                </div>
                                {dismissableCount > 0 && (
                                    <button
                                        type="button"
                                        className={styles['minimized-clear-btn']}
                                        onClick={clearDismissable}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        aria-label="完了・中断のアップロードを閉じる"
                                        title="完了・中断をまとめて閉じる"
                                    >
                                        完了/中断を閉じる
                                    </button>
                                )}
                            </div>
                        ) : (
                            // 展開状態: すべてのアップロードカードを表示
                            visibleProgresses.map((progress, index) => (
                                <UploadProgress
                                    key={progress.clientId || progress.id}
                                    id={progress.id}
                                    fileName={progress.fileName}
                                    progress={progress.progress}
                                    status={progress.status}
                                    error={progress.error}
                                    videoId={progress.videoId}
                                    onClose={() => toggleMinimize()}
                                    onDismiss={
                                        ['completed', 'error', 'cancelled'].includes(progress.status)
                                            ? () => closeUploadProgress(progress.id)
                                            : undefined
                                    }
                                    onPause={() => pauseUpload(progress.id)}
                                    onResume={() => resumeUpload(progress.id)}
                                    onCancel={() => cancelUpload(progress.id)}
                                    index={index}
                                />
                            ))
                        )}
                    </div>
                )}
            </UploadProgressStateContext.Provider>
        </UploadProgressActionsContext.Provider>
    );
};
